import React, { useRef, useState } from 'react';
import Icon from '../AppIcon';

/**
 * MobilePhotoInput — fixes mobile camera capture on iOS and Android.
 *
 * On mobile: renders two distinct buttons:
 *   1. "Take Photo"  — input with capture="environment" (opens camera directly)
 *   2. "Choose Photo" — input WITHOUT capture (opens gallery/file picker)
 *
 * On desktop: renders a drag-and-drop upload zone with a single file picker.
 *
 * Props:
 *   onFilesSelected(files: File[]) — called with selected/captured image files
 *   multiple (bool) — allow multiple files, default true
 *   disabled (bool) — disable inputs
 *   uploading (bool) — show uploading state
 *   label (string) — optional label override
 *   hint (string) — optional hint text
 *   required (bool) — show required asterisk
 *   compact (bool) — smaller layout for inline use
 */
const MobilePhotoInput = ({
  onFilesSelected,
  multiple = true,
  disabled = false,
  uploading = false,
  label,
  hint,
  required = false,
  compact = false,
}) => {
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const desktopInputRef = useRef(null);
  const [dragging, setDragging] = React.useState(false);
  const [typeError, setTypeError] = React.useState('');

  // Keep this list in sync with the `accept` attributes below and with
  // taskCompletionPhotosService.ALLOWED_MIME_TYPES. HEIC/HEIF included for
  // iOS Photos-app captures; GIF intentionally excluded since task evidence
  // photos are stills.
  const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
  const ALLOWED_EXT_RE = /\.(jpe?g|png|webp|heic|heif)$/i;

  const handleFiles = (files) => {
    setTypeError('');
    // iOS Safari sometimes returns File objects with an empty `type` for
    // images shared from Photos. Fall back to the filename extension so
    // valid files aren't silently dropped — but still enforce the allowlist.
    const isAllowed = (f) => {
      if (f?.type && ALLOWED_MIME?.includes(f?.type)) return true;
      if (!f?.type && ALLOWED_EXT_RE?.test(f?.name || '')) return true;
      return false;
    };
    const all = Array.from(files || []);
    const accepted = all?.filter(isAllowed);
    const rejected = all?.length - accepted?.length;

    if (rejected > 0) {
      setTypeError(
        `${rejected} file${rejected === 1 ? '' : 's'} skipped — only JPEG, PNG, WebP, HEIC, and HEIF are supported.`
      );
    }
    if (accepted?.length > 0) {
      onFilesSelected?.(accepted);
    }
    // Reset input values so the same file can be re-selected
    if (cameraInputRef?.current) cameraInputRef.current.value = '';
    if (galleryInputRef?.current) galleryInputRef.current.value = '';
    if (desktopInputRef?.current) desktopInputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragging(false);
    handleFiles(e?.dataTransfer?.files);
  };

  const isDisabled = disabled || uploading;

  return (
    <div className="w-full">
      {/* Mobile layout: two separate action buttons */}
      <div className="flex sm:hidden gap-2">
        {/* Take Photo button — uses capture="environment" to open camera directly */}
        <button
          type="button"
          disabled={isDisabled}
          onClick={() => cameraInputRef?.current?.click()}
          className={`flex-1 flex flex-col items-center justify-center gap-1.5 min-h-[72px] rounded-xl border-2 transition-all font-medium text-sm ${
            isDisabled
              ? 'border-border bg-muted text-muted-foreground cursor-not-allowed opacity-60'
              : 'border-accent bg-accent/5 text-accent hover:bg-accent/10 active:scale-95'
          }`}
        >
          {uploading ? (
            <Icon name="Loader2" size={22} className="animate-spin" />
          ) : (
            <Icon name="Camera" size={22} />
          )}
          <span>{uploading ? 'Uploading…' : 'Take Photo'}</span>
        </button>

        {/* Choose from Gallery button — NO capture attribute, opens file picker / gallery */}
        <button
          type="button"
          disabled={isDisabled}
          onClick={() => galleryInputRef?.current?.click()}
          className={`flex-1 flex flex-col items-center justify-center gap-1.5 min-h-[72px] rounded-xl border-2 transition-all font-medium text-sm ${
            isDisabled
              ? 'border-border bg-muted text-muted-foreground cursor-not-allowed opacity-60'
              : 'border-border bg-background text-foreground hover:border-accent/60 hover:bg-muted/40 active:scale-95'
          }`}
        >
          <Icon name="ImagePlus" size={22} />
          <span>Choose Photo</span>
        </button>

        {/* Hidden camera input — capture="environment" forces camera on mobile */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          capture="environment"
          multiple={multiple}
          className="hidden"
          disabled={isDisabled}
          onChange={e => handleFiles(e?.target?.files)}
        />

        {/* Hidden gallery input — NO capture attribute, shows gallery/file picker */}
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          multiple={multiple}
          className="hidden"
          disabled={isDisabled}
          onChange={e => handleFiles(e?.target?.files)}
        />
      </div>
      {/* Desktop layout: drag-and-drop zone */}
      <div
        className={`hidden sm:flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl transition-all cursor-pointer ${
          compact ? 'p-4 min-h-[80px]' : 'p-6 min-h-[120px]'
        } ${
          isDisabled
            ? 'border-border bg-muted opacity-60 cursor-not-allowed pointer-events-none'
            : dragging
            ? 'border-accent bg-accent/5 scale-[1.01]'
            : 'border-border hover:border-accent/60 hover:bg-muted/30'
        }`}
        onClick={() => !isDisabled && desktopInputRef?.current?.click()}
        onDragOver={e => { e?.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        {uploading ? (
          <Icon name="Loader2" size={compact ? 20 : 28} className="text-accent animate-spin" />
        ) : (
          <Icon name="Upload" size={compact ? 20 : 28} className={dragging ? 'text-accent' : 'text-muted-foreground'} />
        )}
        <div className="text-center">
          <p className={`font-medium text-foreground ${compact ? 'text-xs' : 'text-sm'}`}>
            {uploading ? 'Uploading…' : 'Drag & drop or click to upload'}
          </p>
          {!compact && (
            <p className="text-xs text-muted-foreground mt-0.5">JPEG, PNG, WebP, HEIC · Max 10MB each</p>
          )}
        </div>
        <input
          ref={desktopInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          multiple={multiple}
          className="hidden"
          disabled={isDisabled}
          onChange={e => handleFiles(e?.target?.files)}
        />
      </div>
      {typeError && (
        <p className="text-xs text-destructive mt-1.5">{typeError}</p>
      )}
      {hint && !typeError && (
        <p className="text-xs text-muted-foreground mt-1.5">{hint}</p>
      )}
    </div>
  );
};

export default MobilePhotoInput;
